import { BOOT_FRESH_MS, BOOT_GLOBAL, type BootAnswers, type CodCheckoutInputDto, type StorefrontUpsell } from '@xshop/shared';
import { DEMO_SHOP_SLUG, previewShopSlug, STORE_HOST_SUFFIXES } from './previewVisit';

// Base API configuration; which store a visit is for comes from its own host, never from a default
const API_BASE_URL = '/api/v1';
const REQUEST_TIMEOUT_MS = 15_000;

// What a caller sees when the address named no store; the page turns this into a notice, not a broken shop.
export const NO_STORE_MESSAGE = 'This address does not name a store';

// A refused API call carrying the per-field messages the checkout form shows under its inputs.
export class StorefrontApiError extends Error {
  readonly details?: { fields?: Record<string, string> };
  /** The HTTP status the API answered with, so a caller can tell "not there" from "not now". */
  readonly status?: number;

  constructor(message: string, details?: { fields?: Record<string, string> }, status?: number) {
    super(message);
    this.name = 'StorefrontApiError';
    this.details = details;
    this.status = status;
  }
}

/** The store a visit is for, and whether its own host already named it. */
export interface ActiveStore {
  slug: string;
  /** True when the subdomain named the store, so the API can resolve it from the host it was asked on. */
  fromHost: boolean;
  /** True on a merchant's own domain (villachi.com): only the API knows which store it is, by the host asked on. */
  custom?: boolean;
}

// Reads the store off the subdomain the shopper actually browsed; a preview reads a demo shop instead.
export function getActiveStore(): ActiveStore | null {
  if (typeof window === 'undefined') return null;
  // A theme being tried on is judged against the demo shop it names, never against the address's own store.
  const preview = previewShopSlug();
  if (preview) return { slug: preview, fromHost: false };
  const asked = new URLSearchParams(window.location.search).get('tenant')?.trim();
  if (asked) return { slug: asked.toLowerCase(), fromHost: false };

  const host = window.location.hostname.toLowerCase();
  const suffix = STORE_HOST_SUFFIXES.find((candidate) => host.endsWith(candidate) && host.length > candidate.length);
  if (suffix) return { slug: host.slice(0, -suffix.length), fromHost: true };
  if (host === 'localhost' || host === '127.0.0.1') {
    return import.meta.env.DEV ? { slug: DEMO_SHOP_SLUG, fromHost: false } : null;
  }
  // Any other host is a domain a merchant connected: the API, asked on this same host, says whose it is.
  return { slug: host, fromHost: true, custom: true };
}

// Names the store on a request only when its host did not: a header would otherwise outrank the subdomain.
export function storeHeaders(): Record<string, string> {
  const store = getActiveStore();
  return store && !store.fromHost ? { 'x-tenant-id': store.slug } : {};
}

// The keyset marker a list endpoint returns beside its rows, in `meta.pageInfo`.
export interface StorefrontPageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  nextCursor?: string;
}

// One page of a list endpoint: the rows, plus the cursor that reaches the next one.
export interface StorefrontPage<T> {
  items: T[];
  pageInfo: StorefrontPageInfo;
}

// The response envelope every endpoint answers with, before a caller picks the rows or the page marker.
interface ApiEnvelope<T> {
  success?: boolean;
  data: T;
  meta?: { pageInfo?: StorefrontPageInfo };
  message?: string;
  error?: { message?: string; details?: { fields?: Record<string, string> } };
}

// Answers a read the server already made for this page and wrote into it, while the page is young, or null.
function bootAnswer<T>(path: string, options: RequestInit): ApiEnvelope<T> | null {
  if ((options.method ?? 'GET') !== 'GET' || typeof window === 'undefined' || window.performance.now() > BOOT_FRESH_MS) return null;
  const boot = (window as unknown as Record<string, BootAnswers | undefined>)[BOOT_GLOBAL];
  return (boot?.[path] as ApiEnvelope<T> | undefined) ?? null;
}

// Reads the data of a first answer the page carries, for a hook to start from instead of a loading state — which is also
// what lets the page be drawn on the server; undefined when the page carries none for that path.
export function bootData<T>(path: string): T | undefined {
  return bootAnswer<T>(path, {})?.data;
}

// Sends one request with the tenant header, surfacing a refusal as a typed error; a first read the page carries is answered from it.
async function requestJson<T>(path: string, options: RequestInit): Promise<ApiEnvelope<T>> {
  const booted = bootAnswer<T>(path, options);
  if (booted) return booted;
  const headers = new Headers(options.headers);
  // A multipart body carries its own boundary in the type the browser writes; naming one here breaks it.
  if (!(options.body instanceof FormData)) headers.set('Content-Type', 'application/json');
  // No resolvable store means no call: asking anyway lets the API pick one and paints a shop nobody asked for.
  if (!headers.has('x-tenant-id')) {
    if (!getActiveStore()) throw new StorefrontApiError(NO_STORE_MESSAGE);
    for (const [key, value] of Object.entries(storeHeaders())) headers.set(key, value);
  }

  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers,
    signal: options.signal ?? AbortSignal.timeout(REQUEST_TIMEOUT_MS)
  });

  const json = (await res.json()) as ApiEnvelope<T>;
  if (!res.ok || json.success === false) {
    throw new StorefrontApiError(json.error?.message || json.message || 'API request failed', json.error?.details, res.status);
  }

  return json;
}

// Helper to make JSON API requests with automatic x-tenant-id header injection
export async function fetchApi<T>(path: string, options: RequestInit = {}): Promise<T> {
  const json = await requestJson<T>(path, options);
  return json.data;
}

// Reads one keyset page, keeping the cursor that fetchApi drops so a list can reach page two.
export async function fetchApiPage<T>(path: string, options: RequestInit = {}): Promise<StorefrontPage<T>> {
  const json = await requestJson<T[]>(path, options);
  return {
    items: json.data,
    pageInfo: json.meta?.pageInfo ?? { hasNextPage: false, hasPreviousPage: false }
  };
}

// Product representation for storefront display
export interface StorefrontProduct {
  id: string;
  title: string;
  slug: string;
  descriptionHtml?: string;
  status: string;
  variants: Array<{
    id: string;
    title: string;
    sku?: string;
    price: string;
    compareAtPrice?: string | null;
    inventoryQuantity: number;
    /** False when the merchant does not count this variant's stock, as Shopify's "Track quantity" off. */
    trackInventory?: boolean;
    allowBackorders?: boolean;
    available?: boolean;
    /** The picture this variant shows, Shopify's featured image of a variant. */
    imageId?: string | null;
    option1?: string;
    option2?: string;
  }>;
  images: Array<{
    id: string;
    src: string;
    altText?: string;
    position: number;
  }>;
  /** The approved-review average the server aggregated, and how many made it; 0 when none exist. */
  ratingAverage?: number;
  ratingCount?: number;
  /** The merchant's own type line — "Cream", "Oil" — which a card prints above the name, as the reference does. */
  productType?: string | null;
  vendor?: string | null;
  /** When it was added, so a card can say "New" for a few weeks without anyone typing it. */
  createdAt?: string;
  /** The choices a buyer makes — a product with any is "Choose options", one with none is "Add to cart". */
  options?: Array<{ name: string; values: string[] }>;
}

export interface TenantSettings {
  name: string;
  currency: string;
  plan: string;
  settings: Record<string, any>;
  /** False while the store has no plan running: the shop shows its opening-soon page and takes no orders. */
  open?: boolean;
}

// One rung of a quantity-break ladder, priced by the server so the page never does money maths
export interface StorefrontBundleTier {
  id: string;
  quantity: number;
  tierLabel: string;
  subtext: string | null;
  badgeText: string | null;
  isDefault: boolean;
  pricing: {
    quantity: number;
    unitPrice: number;
    lineTotal: number;
    compareAtTotal: number;
    savingsAmount: number;
    savingsPercent: number;
  };
}

// The active offer on a product page, already resolved against the variant it will be ordered as
export interface StorefrontBundle {
  id: string;
  title: string;
  subtitle: string | null;
  bundleType: string;
  displayStyle: string;
  referenceVariantId: string;
  referenceUnitPrice: number;
  tiers: StorefrontBundleTier[];
}

// Fetches the priced offer for a product page, or null when the store runs none.
export async function getProductBundle(productId: string, variantId?: string): Promise<StorefrontBundle | null> {
  const query = variantId ? `?variantId=${encodeURIComponent(variantId)}` : '';
  return await fetchApi<StorefrontBundle | null>(`/offers/bundles/product/${productId}${query}`);
}

// What the server says delivery costs for a cart, so the page never quotes a fee the order will not charge.
export interface ShippingQuote {
  selectedRate: { id?: string; name: string; price: number; estimatedMinDays: number; estimatedMaxDays: number };
}

// Prices delivery server-side; guessing it in the browser is what let the page show 349 and the order charge 384.
export async function getShippingQuote(subtotalPrice: number, countryCode = 'MA'): Promise<ShippingQuote> {
  return await fetchApi<ShippingQuote>('/shipping/quote', {
    method: 'POST',
    body: JSON.stringify({ subtotalPrice, countryCode })
  });
}

// Fetches the one order bump this product's checkout offers, priced by the server, or null when it runs none.
export async function getProductUpsell(productId: string, excludeOfferId?: string): Promise<StorefrontUpsell | null> {
  return await fetchApi<StorefrontUpsell | null>(`/upsells/for-product/${encodeURIComponent(productId)}${excludeOfferId ? `?excludeOfferId=${encodeURIComponent(excludeOfferId)}` : ''}`);
}

// Fetches active tenant store details and currency
export async function getTenantSettings(): Promise<TenantSettings> {
  return await fetchApi<TenantSettings>('/tenant/settings');
}

// Fetches all catalog products for active tenant
export async function getStoreProducts(): Promise<StorefrontProduct[]> {
  return await fetchApi<StorefrontProduct[]>('/products');
}

// Reads one page of the catalog, for a collection section holding more products than a single page returns.
export async function getStoreProductsPage(limit = 20, cursor?: string, search?: string): Promise<StorefrontPage<StorefrontProduct>> {
  const query = new URLSearchParams({ limit: String(Math.min(Math.max(limit, 1), 100)) });
  if (cursor) query.set('cursor', cursor);
  if (search) query.set('search', search.slice(0, 100));
  return await fetchApiPage<StorefrontProduct>(`/products?${query.toString()}`);
}

// Fetches tenant catalog product by handle
export async function getProductBySlug(slug: string): Promise<StorefrontProduct> {
  return await fetchApi<StorefrontProduct>(`/products/by-slug/${slug}`);
}

// Calculates dynamic shipping rate and price totals for checkout
export async function calculateCheckout(payload: CodCheckoutInputDto): Promise<any> {
  return await fetchApi<any>('/checkouts', {
    method: 'POST',
    body: JSON.stringify(payload)
  });
}

// Submits a 1-page COD checkout directly into an order
export async function submitCodOrder(payload: CodCheckoutInputDto): Promise<any> {
  return await fetchApi<any>('/checkouts/complete', {
    method: 'POST',
    body: JSON.stringify(payload)
  });
}

// Sends a shopper's review, which appears once the merchant approves it.
export async function submitReview(payload: {
  productId: string;
  customerName: string;
  customerPhone?: string;
  customerEmail?: string;
  rating: number;
  title?: string;
  body: string;
  images?: string[];
}): Promise<{ id: string; status: string }> {
  // A shopper's review waits for the merchant's approval; the endpoint never lets it set its own status.
  return await fetchApi<{ id: string; status: string }>('/reviews/storefront', {
    method: 'POST',
    body: JSON.stringify(payload)
  });
}

// Fetches public collections
export async function getStoreCollections(): Promise<any[]> {
  return await fetchApi<any[]>('/collections');
}

// Fetches a single collection with its products
export async function getCollectionBySlug(slug: string): Promise<any> {
  return await fetchApi<any>(`/collections/slug/${slug}`);
}

// Reads one bounded page of a collection's sellable products, in the merchant's merchandising order.
export async function getCollectionProducts(handle: string, limit = 20): Promise<StorefrontProduct[]> {
  const query = new URLSearchParams({ limit: String(Math.min(Math.max(limit, 1), 100)) });
  return await fetchApi<StorefrontProduct[]>(`/collections/${encodeURIComponent(handle)}/products?${query.toString()}`);
}

// A merchant-authored CMS page from the admin editor (e.g. terms, privacy, policy, about)
export interface StorefrontCmsPage {
  id: string;
  title: string;
  slug: string;
  pageType: string;
  status: string;
  bodyHtml?: string | null;
  sectionsData?: { sections: Record<string, any>; order: string[] };
  seoMetadata?: Record<string, any>;
}

// Fetches a published CMS content page by handle slug
export async function getStorePageBySlug(slug: string): Promise<StorefrontCmsPage> {
  return await fetchApi<StorefrontCmsPage>(`/tenant/pages/${encodeURIComponent(slug)}`);
}

/** One link of a store menu, as the menu element draws it. */
export interface StorefrontMenuItem {
  label: string;
  url: string;
}

/** One of the store's navigation menus: what it is called, and the links it holds. */
export interface StorefrontMenu {
  id: string;
  title: string;
  handle: string;
  items: StorefrontMenuItem[];
}

// Reads one navigation menu by the handle a header names; a store without it answers 404.
export async function getStoreMenu(handle: string, signal?: AbortSignal): Promise<StorefrontMenu> {
  return await fetchApi<StorefrontMenu>(`/menus/handle/${encodeURIComponent(handle)}`, { signal });
}

export type MerchandiseQuote = { subtotal: number; lines: Array<{ variantId: string; productId: string; quantity: number; unitPrice: string; totalPrice: string; listUnitPrice?: string; discountAmount?: string; offerLabel?: string }> };
export function quoteMerchandise(lineItems: CodCheckoutInputDto['lineItems']): Promise<MerchandiseQuote> {
  return fetchApi('/checkouts/quote', { method: 'POST', body: JSON.stringify({ lineItems }) });
}
